package one.healthium.insights.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MediumTopAppBar
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.TopAppBarScrollBehavior
import androidx.compose.material3.TopAppBarState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.NabBarStyle
import one.healthium.insights.domain.models.NavbarModel
import one.healthium.insights.ui.models.NavBarUiModel
import one.healthium.ui.NetworkImage
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.components.AppTopBarDefaults
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black
import one.healthium.ui.compose.theme.black50
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.utils.toColor
import org.jetbrains.compose.resources.painterResource

@Composable
internal fun ContentAppTopBar(
    model: NavBarUiModel?,
    state: TopAppBarState? = null,
    scrollBehavior: TopAppBarScrollBehavior? = null,
    onBackClicked: () -> Unit,
    onActionClicked: (ActionModel) -> Unit,
) {
    when (model?.style) {
        NabBarStyle.Default -> DefaultAppBar(
            model = model,
            scrollBehavior = scrollBehavior,
            onBackClicked = onBackClicked,
        )

        NabBarStyle.Large -> LargeAppBar(
            model = model,
            onBackClicked = onBackClicked,
            onActionClicked = onActionClicked,
            scrollBehavior = scrollBehavior,
            state = state,
        )

        null -> {}
    }
}

@Composable
private fun DefaultAppBar(
    model: NavBarUiModel,
    scrollBehavior: TopAppBarScrollBehavior? = null,
    onBackClicked: () -> Unit,
) {
    AppTopBar(
        title = {
            val title = model.title
            if (title != null) {
                Text(title)
            }
        },
        colors = model.color?.let {
            AppTopBarDefaults.colors(
                containerColor = it.toColor(),
                titleContentColor = black,
                navigationIconContentColor = black,
            )
        },
        navigationIcon = {
            AppBackNavigationButton(
                modifier = Modifier.testTag("content_close"),
                onClick = onBackClicked,
            )
        },
        scrollBehavior = scrollBehavior,
    )
}

@Composable
private fun LargeAppBar(
    model: NavBarUiModel,
    state: TopAppBarState? = null,
    scrollBehavior: TopAppBarScrollBehavior? = null,
    onBackClicked: () -> Unit,
    onActionClicked: (ActionModel) -> Unit,
) {
    val isCollapsed by remember {
        derivedStateOf { state?.collapsedFraction == 1.0f }
    }

    Box(
        modifier = Modifier.fillMaxWidth(),
    ) {
        Box {
            AnimatedVisibility(
                modifier = Modifier.matchParentSize(),
                visible = !isCollapsed,
                enter = fadeIn(),
                exit = fadeOut(),
            ) {
                if (model.image != null) {
                    NetworkImage(
                        modifier = Modifier
                            .fillMaxSize()
                            .clip(RoundedCornerShape(bottomStart = 6.dp, bottomEnd = 6.dp)),
                        imageUrl = model.image,
                        contentScale = ContentScale.Crop,
                        alignment = Alignment.BottomCenter,
                    )
                } else if (model.color != null) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .clip(RoundedCornerShape(bottomStart = 6.dp, bottomEnd = 6.dp))
                            .background(model.color.toColor()),
                    )
                }
            }
            MediumTopAppBar(
                title = { },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color.Transparent,
                    scrolledContainerColor = Color.Transparent
                ),
                collapsedHeight = 46.dp, // height of regular appbar
                expandedHeight = 256.dp,
                scrollBehavior = scrollBehavior,
            )
        }

        Row(
            modifier = Modifier
                .statusBarsPadding()
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 2.dp)
                .padding(bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Box(
                modifier = Modifier
                    .testTag("content_close")
                    .size(36.dp)
                    .clip(CircleShape)
                    .clickable(onClick = onBackClicked)
                    .background(black50),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(AppIcons.icNavigationBack),
                    tint = white,
                    contentDescription = null,
                )
            }
            Spacer(Modifier.weight(1f))
            ContentAppBarActions(
                uiModel = model,
                onActionClicked = onActionClicked
            )
        }
    }
}

@Composable
@Preview(showBackground = true)
private fun ContentAppTopBarPreview() {
    AppTheme {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            ContentAppTopBar(
                model = NavBarUiModel(
                    model = NavbarModel(
                        title = "Title",
                        style = NabBarStyle.Default,
                        color = 0xFFFFFFFF,
                        image = null,
                        actions = null,
                    ),
                    savedContentModel = null,
                ),
                onBackClicked = {},
                onActionClicked = {},
            )

            ContentAppTopBar(
                model = NavBarUiModel(
                    model = NavbarModel(
                        title = "Title",
                        style = NabBarStyle.Large,
                        color = 0xFFFF0000,
                        image = null,
                        actions = null,
                    ),
                    savedContentModel = null,
                ),
                onBackClicked = {},
                onActionClicked = {},
            )
        }
    }
}